import {Action, getModule, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import store from '@/store';
import {find, pick, isArray, remove} from 'lodash';
import {findIndex} from 'lodash';
import {Vue} from 'vue-property-decorator';

export interface ITicketStoreState {
	tickets: ITicket[];
}

export interface ITotalDetail {
	name: string;
	value: number;
}

export interface ISDPPayment {
	paymentId: string;
	amount: number;
	gratuity: number;
	paidDate: number;
	voidedDate: number | null;
	logo: string;
	last4: string;
	processorDetails: {
		[key: string]: any;
	};
}

export interface IPOSPayment {
	paymentId: string;
	amount: number;
	gratuity: number;
	paidDate: number;
	voidedDate: number | null;
}

export interface ILineItem {
	locationId: string;
	ticketId: string;
	courseId: number;
	itemId: string;
	menuId: string;
	lineItemId: number;
	options: ILineItemOption[];
	comments: string | null;
	status: 'PRE_ORDERED' | 'ORDERED' | 'CLAIMED' | 'PREPARED' | 'STAGED' | 'DELIVERED' | 'CANCELLED';
	total: number;
	modId: number;
}

export interface ILineItemOption {
	optionId: string;
	choices: ILineItemOptionChoice[];
}

export interface ILineItemOptionChoice {
	choiceId: string;
	quantity: number;
}


export interface IItemModifier {
	name: string;
	comment: string;
	quantity: number;
}

export interface IItem {
	name: string;
	comment: string;
	quantity: number;
	price: number;
	modifiers: IItemModifier[];
}

export interface ICustomer {
	id: string | null;
	name: string | null;
	phone: string | null;
}

export interface ITicket {
	ticketId: string;
	payUrl: string;
	openedDate: number;
	arrivedDate: number | null;
	readyDate: number | null;
	closedDate: number | null;
	total: number | null;
	totalDetails: ITotalDetail[];
	payments?: Array<ISDPPayment | IPOSPayment>;
	amountDue: number;
	items: IItem[] | null;
	lineItems: ILineItem[] | null;
	isOrderingAvailable: boolean;
	isOrderingLocked: boolean;
	serverName: string;
	tableName: string;
	badgeId: string | null;
	customer: ICustomer;
	waitTime: number | null;
	partySize: number;
	orderNotes: string | null;
	modId: number;
	state: 'OPEN' | 'SENT' | 'VIEWED' | 'UNDERPAID' | 'PAID' | 'CANCELLED';
}


@Module({dynamic: true, store, name: 'ticket', namespaced: true})
class Ticket extends VuexModule implements ITicketStoreState {
	tickets: ITicket[] = [];

	public static mergeAndAdd(existingItems: any[], newItems: any[], predicateKeys: string[]) {
		newItems.forEach((newItem) => {
			const existingItemIndex = findIndex(existingItems, pick(newItem, predicateKeys));
			if(existingItemIndex > -1) {
				Vue.set(existingItems, existingItemIndex, Object.assign({}, existingItems[existingItemIndex], newItem));
			} else {
				existingItems.push(newItem);
			}
		});
	}

	get byId() {
		return (ticketId: string): ITicket | null => {
			return find(this.tickets, {ticketId: ticketId}) || null;
		};
	}

	get active() {
		return this.tickets.filter((ticket) => {
			return ticket.waitTime === null;
		});
	}

	get waitlist() {
		return this.tickets.filter((ticket) => {
			return ticket.waitTime !== null;
		});
	}

	get currentlyWaiting() {
		return this.waitlist.reduce((totals, ticket) => {
			totals.groups++;
			totals.guests += ticket.partySize;
			return totals;
		}, {
			groups: 0,
			guests: 0
		});
	}

	get ticketByBadgeId() {
		return (badgeId: string): ITicket | null => {
			return find(this.tickets, {badgeId: badgeId}) || null;
		};
	}

	@Action
	public addOrUpdateTicket(ticket: ITicket | ITicket[]) {
		if(!isArray(ticket)) {
			ticket = [ticket];
		}
		// else {
		// 	this.CLEAR_TICKETS();
		// }
		this.ADD_OR_UPDATE_TICKET(ticket.map((item) => {
			if(item.partySize !== null) {
				item.partySize = Number(item.partySize);
			}
			if(item.customer.name && item.customer.name.indexOf(', ') !== -1) {
				item.customer.name = item.customer.name.split(', ').reverse().join(' ');
			}
			if(item.customer.phone) {
				item.customer.phone = item.customer.phone.replace(/[^0-9]/g, '');
				if(!item.customer.phone.startsWith('1')) {
					item.customer.phone = '1' + item.customer.phone;
				}
			}
			return item;
		}));
	}

	@Action
	public removeTicket(ticketId: string) {
		this.REMOVE_TICKET(ticketId);
	}

	@Action
	public clearTickets() {
		this.CLEAR_TICKETS();
	}

	@Mutation
	public ADD_OR_UPDATE_TICKET(tickets: ITicket[]) {
		Ticket.mergeAndAdd(this.tickets, tickets, ['ticketId']);
	}

	@Mutation
	public REMOVE_TICKET(ticketId: string) {
		const foundIndex = findIndex(this.tickets, {ticketId: ticketId});
		if(foundIndex !== -1) {
			this.tickets.splice(foundIndex, 1);
		}
	}

	@Mutation
	public CLEAR_TICKETS() {
		this.tickets = [];
	}
}

export const TicketStore = getModule(Ticket);
